import React, { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { Check, ArrowRight, GraduationCap, Sparkles, Clock } from 'lucide-react';
import { BATCH_DATE } from '@/config/batch';
import { z } from 'zod';

// Strict server-friendly validation: bound every free-text field.
const applySchema = z.object({
  studentName: z.string().trim().min(1, "Student name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().min(1, "Email is required").email("Email is invalid").max(255, "Email must be less than 255 characters"),
  whatsappNumber: z.string().trim().min(1, "WhatsApp number is required").regex(/^\+?[0-9\s\-()]{7,20}$/, "WhatsApp number is invalid"),
  schoolName: z.string().trim().min(1, "School name is required").max(150, "School name must be less than 150 characters"),
  grade: z.string().min(1, "Grade is required"),
  improvementArea: z.string().min(1, "Please select an area").max(100),
  city: z.string().trim().max(100, "City must be less than 100 characters").optional().or(z.literal('')),
  heardAboutUs: z.string().max(50).optional().or(z.literal('')),
});

const PAYMENT_LINK = "https://pages.razorpay.com/aiacademy";

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(15 * 60);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => setTimeLeft(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  const mins = Math.floor(timeLeft / 60);
  const secs = timeLeft % 60;

  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
      <div className="flex items-center justify-center gap-2 text-red-400 mb-1">
        <Clock className="w-4 h-4" />
        <span className="text-sm font-bold">Your seat is reserved for:</span>
      </div>
      <p className="text-3xl font-black text-red-400 tabular-nums">
        {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
      </p>
      <p className="text-gray-500 text-xs mt-1">Complete payment to confirm your spot</p>
    </div>
  );
};

const Apply = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    email: '',
    whatsappNumber: '',
    schoolName: '',
    grade: '',
    improvementArea: '',
    city: '',
    heardAboutUs: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const validateForm = () => {
    const result = applySchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        if (field && !newErrors[field]) newErrors[field] = err.message;
      });
      setErrors(newErrors);
      return null;
    }
    setErrors({});
    return result.data;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validated = validateForm();
    if (!validated) return;
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('student_applications')
        .insert({
          student_name: validated.studentName,
          email: validated.email,
          whatsapp_number: validated.whatsappNumber,
          school_name: validated.schoolName,
          grade: validated.grade,
          improvement_area: validated.improvementArea,
          city: validated.city || '',
          heard_about_us: validated.heardAboutUs || ''
        });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Application Submitted! 🎉",
        description: "You're one step away from transforming your child's future.",
      });
    } catch (error) {
      console.error('Application error:', error);
      toast({
        title: "Submission Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full px-4 py-3 rounded-lg border ${errors[field] ? 'border-red-500' : 'border-white/10'} bg-white/[0.06] text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 transition-colors`;

  const selectClass = (field: string) =>
    `w-full px-4 py-3 rounded-lg border ${errors[field] ? 'border-red-500' : 'border-white/10'} bg-white/[0.06] text-white focus:outline-none focus:ring-2 focus:ring-green-500/50 focus:border-green-500/50 transition-colors [&>option]:bg-[#111] [&>option]:text-white`;

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white/[0.04] border border-white/10 rounded-2xl p-8 text-center space-y-6">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-3xl font-bold text-white">Application Received! 🎉</h2>
          <p className="text-gray-400 text-lg">
            Thank you, <span className="font-semibold text-white">{formData.studentName}</span>'s parent! Your application has been submitted successfully.
          </p>

          <CountdownTimer />

          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 space-y-3">
            <Sparkles className="w-8 h-8 text-green-400 mx-auto" />
            <h3 className="text-xl font-bold text-white">Complete Your Enrollment</h3>
            <p className="text-gray-400 text-sm">
              Secure your child's spot in the AI Smart-Student Program by completing the payment below.
            </p>
          </div>
          <a
            href={PAYMENT_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white text-lg font-bold py-4 px-8 rounded-xl shadow-lg transition-all transform hover:scale-[1.02]"
          >
            <span>Complete Payment & Enroll Now</span>
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="text-xs text-gray-600">Secure payment powered by Razorpay</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Urgency + Value Reminder */}
        <div className="mb-6 space-y-3">
          <div className="bg-red-600 py-2.5 px-4 rounded-xl text-center">
            <p className="text-white text-sm font-semibold">🔴 Next Batch Starts {BATCH_DATE} — Only a Few Seats Left</p>
          </div>
          <div className="bg-green-600/10 border border-green-500/20 rounded-xl py-3 px-4 text-center">
            <p className="text-green-400 text-sm font-bold">₹2,999 only · 100% refund after first 2 sessions · Zero risk</p>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-xl p-5 space-y-3">
            <p className="text-white font-bold text-sm">What your child gets in 8 weekends:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 flex-shrink-0" /> AI-powered study system</span>
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 flex-shrink-0" /> Personal AI study coach</span>
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 flex-shrink-0" /> Build real websites & apps</span>
              <span className="flex items-center gap-1.5"><Check className="w-3 h-3 text-green-500 flex-shrink-0" /> Certificate of completion</span>
            </div>
            <p className="text-gray-500 text-xs italic border-t border-white/[0.06] pt-3">
              "My son went from average to top 5 in his class. Teachers started asking what changed." — Priya M.
            </p>
          </div>
        </div>

        <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white/[0.06] text-gray-300 px-4 py-2 rounded-full text-sm font-bold mb-4 border border-white/10">
              <GraduationCap className="h-4 w-4" />
              AI SMART-STUDENT PROGRAM
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Enrol Now — Secure Your Child's Spot</h1>
            <p className="text-gray-400 text-sm">We review every application to ensure this is the right fit for your child.</p>
          </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Student Full Name *</label>
              <input type="text" name="studentName" maxLength={100} value={formData.studentName} onChange={handleChange} className={inputClass('studentName')} placeholder="Enter student's full name" />
              {errors.studentName && <p className="text-red-400 text-xs mt-1">{errors.studentName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
              <input type="email" name="email" maxLength={255} value={formData.email} onChange={handleChange} className={inputClass('email')} placeholder="parent@example.com" />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">WhatsApp Number *</label>
              <input type="tel" name="whatsappNumber" maxLength={20} value={formData.whatsappNumber} onChange={handleChange} className={inputClass('whatsappNumber')} placeholder="+91 98502 XXXXX" />
              {errors.whatsappNumber && <p className="text-red-400 text-xs mt-1">{errors.whatsappNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">School Name *</label>
              <input type="text" name="schoolName" maxLength={150} value={formData.schoolName} onChange={handleChange} className={inputClass('schoolName')} placeholder="Enter school name" />
              {errors.schoolName && <p className="text-red-400 text-xs mt-1">{errors.schoolName}</p>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Grade *</label>
              <select name="grade" value={formData.grade} onChange={handleChange} className={selectClass('grade')}>
                <option value="">Select grade</option>
                {Array.from({ length: 7 }, (_, i) => i + 6).map(g => (
                  <option key={g} value={`Grade ${g}`}>Grade {g}</option>
                ))}
              </select>
              {errors.grade && <p className="text-red-400 text-xs mt-1">{errors.grade}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">City (Location) <span className="text-gray-600">(Optional)</span></label>
              <input type="text" name="city" maxLength={100} value={formData.city} onChange={handleChange} className={inputClass('city')} placeholder="e.g., Mumbai, Delhi" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">What do you want your child to improve the most? *</label>
            <select name="improvementArea" value={formData.improvementArea} onChange={handleChange} className={selectClass('improvementArea')}>
              <option value="">Select an area</option>
              <option value="Academic Performance">Academic Performance</option>
              <option value="Critical Thinking & Problem Solving">Critical Thinking & Problem Solving</option>
              <option value="Communication Skills">Communication Skills</option>
              <option value="Creativity & Innovation">Creativity & Innovation</option>
              <option value="Technology & AI Skills">Technology & AI Skills</option>
              <option value="Time Management & Discipline">Time Management & Discipline</option>
              <option value="Confidence & Leadership">Confidence & Leadership</option>
              <option value="Career Readiness">Career Readiness</option>
            </select>
            {errors.improvementArea && <p className="text-red-400 text-xs mt-1">{errors.improvementArea}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">How did you hear about us? <span className="text-gray-600">(Optional)</span></label>
            <select name="heardAboutUs" value={formData.heardAboutUs} onChange={handleChange} className={selectClass('heardAboutUs')}>
              <option value="">Select an option</option>
              <option value="Instagram">Instagram</option>
              <option value="Facebook">Facebook</option>
              <option value="YouTube">YouTube</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="Google Search">Google Search</option>
              <option value="Friend / Family">Friend / Family</option>
              <option value="School / Teacher">School / Teacher</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 py-4 rounded-xl text-white text-lg font-bold transition-all shadow-lg disabled:opacity-50"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                <span>Submitting...</span>
              </div>
            ) : (
              <>
                <Check className="w-5 h-5" />
                <span>Submit Application</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="text-center text-xs text-gray-500 mt-3">
            Seats are confirmed on a first-come, first-served basis.
          </p>
          <p className="text-center text-xs text-gray-600 mt-1">
            Your information is secure and will only be used for enrollment purposes.
          </p>
        </form>
        </div>
      </div>
    </div>
  );
};

export default Apply;
